import "@/App.css";
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronUp, ChevronDown, Instagram, Youtube, X } from "lucide-react";
import { Navbar } from "./components/Navbar";
import { HeroSection } from "./components/HeroSection";
import { WhySoundmoo } from "./components/WhySoundmoo";
import { CharacterShowcase } from "./components/CharacterShowcase";
import { SoundIdentity } from "./components/SoundIdentity";
import { Partnerships } from "./components/Partnerships";
import { ContactSection } from "./components/ContactSection";
import { DreamBackground, SkyForeground } from "./components/DreamBackground";
import { CookieConsent } from "./components/CookieConsent";
import { BottomGoKarts } from "./components/FloatingCharacters";

const COW_LOGO = "https://customer-assets.emergentagent.com/job_461f173b-2f7b-4524-a861-72bba2ed6414/artifacts/1nwnksnx_Minimalist%20_moo_%20logo%20design.png";
const SOUNDMOO_TEXT_LOGO = "https://customer-assets.emergentagent.com/job_character-licensing/artifacts/hag509ui_Soundmoo%20txt%20TS%20WHITE%281%29.png";
const COSMIX_POSTER = "https://customer-assets.emergentagent.com/job_461f173b-2f7b-4524-a861-72bba2ed6414/artifacts/sursbt6o_PHOTO-2026-03-17-16-37-38.jpg";
const COSMIX_SONG_ARTWORK = "https://customer-assets.emergentagent.com/job_461f173b-2f7b-4524-a861-72bba2ed6414/artifacts/p9m7qvbz_Cosmix%20Artwork.png";
const COSMIX_LOGO = "https://customer-assets.emergentagent.com/job_461f173b-2f7b-4524-a861-72bba2ed6414/artifacts/nskmtkd0_Cosmix%20Logo.png";
const MOMO_ALBUM_ARTWORK = "https://customer-assets.emergentagent.com/job_ip-character-hub/artifacts/0thjhhzk_Soundmoo_Volume1_3000x3000_Final.png";
const MOMO_LOGO = "https://customer-assets.emergentagent.com/job_461f173b-2f7b-4524-a861-72bba2ed6414/artifacts/w9xja97m_MOMO%20Logo%20Intro%20TS.png";
const TRK_EVERYWHERE_ARTWORK = "https://customer-assets.emergentagent.com/job_c9ad24c8-9a40-422b-823a-575f7c88504d/artifacts/9w0yf8wu_Everywhere-art.jpg";
const TRK_LOGO = "https://customer-assets.emergentagent.com/job_c9ad24c8-9a40-422b-823a-575f7c88504d/artifacts/ouxmby5o_TRK%20FINAL%20LOGO%20full%20RES.png";
const LONDON_ARTWORK = "https://customer-assets-gfyr7b9c.emergentagent.net/job_02fb1ea7-05bc-49a3-acd9-1025e0802bad/artifacts/hyk0bonl_LondonArtwork.jpg";
const LONDON_POPUP_ARTWORK = LONDON_ARTWORK;
const SPOTIFY_COSMIX_URL = "https://open.spotify.com/track/16S6i3t313VeKNdhI1nWLj?si=8fcec74db2bd4dd9";

// Star Icon component
const StarIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);

// X (Twitter) Icon component
const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// Confetti Component
const Confetti = ({ colors = ['#FF69B4', '#FF1493', '#FF00FF', '#00FFFF', '#00FF00', '#FFFF00'] }) => {
  const confettiPieces = [...Array(100)].map((_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 0.5,
    duration: 2 + Math.random() * 2,
    color: colors[Math.floor(Math.random() * colors.length)],
    size: 8 + Math.random() * 12,
    rotation: Math.random() * 360,
  }));

  return (
    <div className="fixed inset-0 pointer-events-none z-[200] overflow-hidden">
      {confettiPieces.map((piece) => (
        <motion.div
          key={piece.id}
          className="absolute"
          style={{
            left: `${piece.x}%`,
            top: -20,
            width: piece.size,
            height: piece.size * 0.6,
            backgroundColor: piece.color,
            borderRadius: '2px',
            boxShadow: `0 0 10px ${piece.color}, 0 0 20px ${piece.color}`,
          }}
          initial={{ y: -20, rotate: 0, opacity: 1 }}
          animate={{
            y: '100vh',
            rotate: piece.rotation + 720,
            opacity: [1, 1, 0],
          }}
          transition={{
            duration: piece.duration,
            delay: piece.delay,
            ease: 'easeIn',
          }}
        />
      ))}
    </div>
  );
};

// Cosmix Popup Advertisement Modal
const CosmixPopupAd = ({ onMinimize }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-start md:items-center justify-center p-2 md:p-4 bg-black/70 backdrop-blur-sm overflow-y-auto"
      onClick={onMinimize}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="relative max-w-3xl w-full my-4 md:my-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button - Fixed position inside on mobile */}
        <button
          onClick={onMinimize}
          className="absolute top-2 right-2 md:-top-3 md:-right-3 z-20 w-10 h-10 rounded-full bg-white text-black flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors"
          data-testid="cosmix-popup-close-button"
        >
          <X className="w-6 h-6" />
        </button>
        
        {/* Content */}
        <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-[#FF69B4]/20 to-[#9B59B6]/20 backdrop-blur-md p-4 md:p-6 border border-white/20">
          {/* Soundmoo Logo at top */}
          <div className="flex justify-center mb-4 pt-8 md:pt-0">
            <img
              src={SOUNDMOO_TEXT_LOGO}
              alt="Soundmoo"
              className="h-12 md:h-20"
            />
          </div>
          
          {/* Out Now Badge */}
          <div className="flex justify-center mb-4">
            <div className="bg-[#FF69B4] text-white px-4 py-2 rounded-full font-bold flex items-center gap-2 shadow-lg">
              <StarIcon className="w-4 h-4" />
              OUT NOW
              <StarIcon className="w-4 h-4" />
            </div>
          </div>
          
          {/* Content Grid - Poster + Artwork */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Main Poster */}
            <div className="rounded-xl overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.3)]">
              <img
                src={COSMIX_POSTER}
                alt="Cosmix Girlz - Season 1"
                className="w-full h-auto"
              />
            </div>
            
            {/* Song Artwork & Info */}
            <div className="flex flex-col justify-center">
              <div className="rounded-xl overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.3)] mb-4">
                <img
                  src={COSMIX_SONG_ARTWORK}
                  alt="Accessorize - Single"
                  className="w-full h-auto"
                />
              </div>
              <div className="text-center">
                <p className="text-white/80 text-sm mb-2">Debut Single</p>
                <h3 className="text-white font-bold text-2xl font-['Outfit'] mb-1">Accessorize</h3>
                <p className="text-[#FF69B4] text-sm font-medium">Available Now</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* CTA Button - Links to Spotify */}
        <motion.a
          href={SPOTIFY_COSMIX_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 w-full py-3 rounded-full bg-gradient-to-r from-[#FF69B4] to-[#9B59B6] text-white font-semibold text-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
          </svg>
          Listen on Spotify
        </motion.a>
      </motion.div>
    </motion.div>
  );
};

// Momo Popup Advertisement Modal
const MomoPopupAd = ({ onMinimize }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-start md:items-center justify-center p-2 md:p-4 bg-black/70 backdrop-blur-sm overflow-y-auto"
      onClick={onMinimize}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="relative max-w-3xl w-full my-4 md:my-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button - Fixed position inside on mobile */}
        <button
          onClick={onMinimize}
          className="absolute top-2 right-2 md:-top-3 md:-right-3 z-20 w-10 h-10 rounded-full bg-white text-black flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors"
          data-testid="momo-popup-close-button"
        >
          <X className="w-6 h-6" />
        </button>
        
        {/* Content */}
        <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-[#19A4FB]/20 to-[#0A1A3F]/40 backdrop-blur-md p-4 md:p-6 border border-white/20">
          {/* Soundmoo Logo at top */}
          <div className="flex justify-center mb-4 pt-8 md:pt-0">
            <img
              src={SOUNDMOO_TEXT_LOGO}
              alt="Soundmoo"
              className="h-12 md:h-20"
            />
          </div>
          
          {/* Milestone Celebration */}
          <motion.div 
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, type: "spring", damping: 15 }}
            className="flex justify-center mb-4"
          >
            <div className="bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] text-[#0A1A3F] px-4 py-2 rounded-full font-bold flex items-center gap-2 shadow-lg">
              <StarIcon className="w-5 h-5" />
              Celebrating 10,000 Plays!
              <StarIcon className="w-5 h-5" />
            </div>
          </motion.div>
          
          {/* Content Grid - matching Cosmix layout */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Album Artwork - Left */}
            <div className="relative rounded-xl overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.3)]">
              <img
                src={MOMO_ALBUM_ARTWORK}
                alt="Sleep & Emotional Comfort"
                className="w-full h-auto"
              />
              {/* 10K Badge on artwork */}
              <div className="absolute top-2 right-2 bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] text-[#0A1A3F] px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
                <StarIcon className="w-3 h-3" />
                10K PLAYS
              </div>
            </div>
            
            {/* Info - Right */}
            <div className="flex flex-col justify-center items-center">
              <div className="text-center">
                <p className="text-white/80 text-sm mb-2">First Album • Out Now</p>
                <h3 className="text-white font-bold text-3xl font-['Outfit'] mb-2">Sleep & Emotional Comfort</h3>
                <p className="text-[#19A4FB] text-sm font-medium mb-4">Available on all platforms</p>
                <p className="text-white/70 text-sm leading-relaxed">
                  A soothing collection of sounds designed to help children relax, sleep, and find emotional comfort.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* CTA Button */}
        <motion.a
          href="https://open.spotify.com/album/1MNkDqgLAi9ZVU69z92NvE"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 w-full py-3 rounded-full bg-gradient-to-r from-[#19A4FB] to-[#0A1A3F] text-white font-semibold text-lg hover:opacity-90 transition-opacity flex items-center justify-center"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Listen Now on Spotify
        </motion.a>
      </motion.div>
    </motion.div>
  );
};

// TRK Popup Advertisement Modal
const TRKPopupAd = ({ onMinimize }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-start md:items-center justify-center p-2 md:p-4 bg-black/70 backdrop-blur-sm overflow-y-auto"
      onClick={onMinimize}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="relative max-w-3xl w-full my-4 md:my-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button - Fixed position inside on mobile */}
        <button
          onClick={onMinimize}
          className="absolute top-2 right-2 md:-top-3 md:-right-3 z-20 w-10 h-10 rounded-full bg-white text-black flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors"
          data-testid="trk-popup-close-button"
        >
          <X className="w-6 h-6" />
        </button>
        
        {/* Content */}
        <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-[#FF6B00]/20 to-[#22C55E]/20 backdrop-blur-md p-4 md:p-6 border border-white/20">
          {/* Soundmoo Logo at top */}
          <div className="flex justify-center mb-4 pt-8 md:pt-0">
            <img
              src={SOUNDMOO_TEXT_LOGO}
              alt="Soundmoo"
              className="h-12 md:h-20"
            />
          </div>
          
          {/* Milestone Celebration */}
          <motion.div 
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, type: "spring", damping: 15 }}
            className="flex justify-center mb-4"
          >
            <div className="bg-gradient-to-r from-[#FF6B00] to-[#22C55E] text-white px-4 py-2 rounded-full font-bold flex items-center gap-2 shadow-lg">
              <StarIcon className="w-5 h-5" />
              Over 200,000 Streams!
              <StarIcon className="w-5 h-5" />
            </div>
          </motion.div>
          
          {/* Content Grid */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Album Artwork - Left */}
            <div className="relative rounded-xl overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.3)]">
              <img
                src={TRK_EVERYWHERE_ARTWORK}
                alt="Everywhere - The Retro Kid"
                className="w-full h-auto"
              />
              {/* 200K Badge on artwork */}
              <div className="absolute top-2 right-2 bg-gradient-to-r from-[#FF6B00] to-[#22C55E] text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
                <StarIcon className="w-3 h-3" />
                200K+ PLAYS
              </div>
            </div>
            
            {/* Info - Right */}
            <div className="flex flex-col justify-center items-center">
              <div className="text-center">
                <img 
                  src={TRK_LOGO} 
                  alt="The Retro Kid" 
                  className="h-16 md:h-20 mx-auto mb-2"
                />
                <p className="text-white/80 text-sm mb-2">Featured Single • Milestone Hit</p>
                <h3 className="text-white font-bold text-2xl font-['Outfit'] mb-2">Everywhere (feat. TALIA)</h3>
                <p className="text-[#22C55E] text-sm font-medium mb-4">Released via XPLODED</p>
                <p className="text-white/70 text-sm leading-relaxed">
                  A nostalgic anthem blending retro synths with modern production.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* CTA Button */}
        <motion.a
          href="https://open.spotify.com/album/0m07LLsC3kLf2utJoJSCwz?si=a_Jz_JCmSZqFjNmJBDeNIw"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 w-full py-3 rounded-full bg-gradient-to-r from-[#FF6B00] to-[#22C55E] text-white font-semibold text-lg hover:opacity-90 transition-opacity flex items-center justify-center"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Stream on Spotify - 200K+ Plays
        </motion.a>
      </motion.div>
    </motion.div>
  );
};

// Hanging With Me Popup Advertisement Modal
// LONDON Release Popup - First exclusive Soundmoo release
const LondonPopupAd = ({ onMinimize }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-start md:items-center justify-center p-2 md:p-4 bg-black/80 backdrop-blur-md overflow-y-auto"
      onClick={onMinimize}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="relative max-w-md w-full my-4 md:my-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onMinimize}
          className="absolute top-2 right-2 md:-top-3 md:-right-3 z-20 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white flex items-center justify-center shadow-lg hover:bg-white/20 transition-colors"
          data-testid="hanging-popup-close-button"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Content Card */}
        <div className="rounded-2xl overflow-hidden bg-gradient-to-b from-[#060d1f] via-[#0a1a3f] to-[#0d0d1a] border border-[#1D9AE8]/20 shadow-[0_0_60px_rgba(29,154,232,0.15)]">
          {/* Soundmoo Logo at top */}
          <div className="flex justify-center pt-6 pb-3 px-4">
            <img
              src={SOUNDMOO_TEXT_LOGO}
              alt="Soundmoo"
              className="h-10 md:h-12"
            />
          </div>
          
          {/* First Exclusive Release Badge */}
          <motion.div 
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, type: "spring", damping: 15 }}
            className="flex justify-center mb-4"
          >
            <div className="bg-gradient-to-r from-[#1D9AE8] to-[#9b59b6] text-white px-4 py-1.5 rounded-full font-bold text-xs tracking-widest flex items-center gap-2 shadow-[0_0_20px_rgba(29,154,232,0.3)]">
              <StarIcon className="w-4 h-4" />
              FIRST EXCLUSIVE RELEASE
              <StarIcon className="w-4 h-4" />
            </div>
          </motion.div>
          
          {/* LONDON Artwork — Hero of the popup */}
          <div className="flex justify-center px-6 mb-4">
            <div className="relative rounded-xl overflow-hidden shadow-[0_10px_50px_rgba(29,154,232,0.25),0_0_30px_rgba(155,89,182,0.15)]">
              <img
                src={LONDON_ARTWORK}
                alt="LONDON - Soundmoo feat. The Retro KiD"
                className="w-full h-auto rounded-xl"
              />
              <div className="absolute inset-0 rounded-xl pointer-events-none" style={{ boxShadow: 'inset 0 0 40px rgba(29,154,232,0.1)' }} />
            </div>
          </div>
          
          {/* Release Info */}
          <div className="text-center px-6 pb-1">
            <h3 className="text-white font-bold text-3xl md:text-4xl font-['Outfit'] mb-1 tracking-tight">
              LONDON
            </h3>
            <p className="text-[#1D9AE8] font-semibold text-sm mb-3">Soundmoo feat. The Retro KiD</p>
            
            {/* OUT NOW */}
            <p className="text-white font-black text-lg tracking-widest mb-1">OUT NOW!</p>
            <p className="text-white/50 text-xs tracking-wider mb-3">Streaming on all platforms.</p>
            
            {/* Platform logos */}
            <div className="flex items-center justify-center gap-5 mb-4">
              {/* Spotify */}
              <svg viewBox="0 0 168 168" className="w-7 h-7" fill="#1DB954">
                <path d="M83.996.277C37.747.277.253 37.77.253 84.019c0 46.251 37.494 83.741 83.743 83.741 46.254 0 83.744-37.49 83.744-83.741C167.74 37.77 130.25.277 83.996.277zm38.404 120.78a5.217 5.217 0 01-7.18 1.73c-19.662-12.01-44.414-14.73-73.564-8.07a5.222 5.222 0 01-6.249-3.93 5.213 5.213 0 013.926-6.25c31.9-7.291 59.263-4.15 81.337 9.34 2.46 1.51 3.24 4.72 1.73 7.18zm10.25-22.805c-1.89 3.075-5.91 4.045-8.98 2.155-22.51-13.839-56.823-17.846-83.448-9.764-3.453 1.043-7.1-.903-8.148-4.35a6.538 6.538 0 014.354-8.143c30.413-9.228 68.222-4.758 94.072 11.127 3.07 1.89 4.04 5.91 2.15 8.976zm.88-23.744c-26.99-16.031-71.52-17.505-97.29-9.684-4.138 1.255-8.514-1.081-9.768-5.219a7.835 7.835 0 015.221-9.771c29.581-8.98 78.756-7.245 109.83 11.202a7.823 7.823 0 012.74 10.733 7.823 7.823 0 01-10.733 2.74z"/>
              </svg>
              {/* YouTube */}
              <svg viewBox="0 0 24 24" className="w-7 h-7" fill="#FF0000">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </div>
            
            <p className="text-white/60 text-sm italic leading-relaxed max-w-xs mx-auto mb-1">
              The Official LONDON Video is out now.
            </p>
          </div>
          
          {/* CTA — Watch Official Video */}
          <div className="px-6 pb-6 pt-2">
            <motion.a
              href="https://youtu.be/FsHxsMBWjsg?si=xMKnDJaXYqOOBEkf"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-3 rounded-full bg-gradient-to-r from-[#1D9AE8] to-[#9b59b6] text-white font-bold text-sm tracking-widest text-center shadow-[0_0_25px_rgba(29,154,232,0.3)] flex items-center justify-center gap-2"
              whileHover={{ scale: 1.02, boxShadow: '0 0 35px rgba(29,154,232,0.5)' }}
              whileTap={{ scale: 0.98 }}
              data-testid="london-video-cta"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              WATCH THE OFFICIAL VIDEO
            </motion.a>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// YouTube Promo Badge - compact circle that expands on hover
const YouTubePromoBadge = () => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.a
      href="https://www.youtube.com/@soundmoo/featured"
      target="_blank"
      rel="noopener noreferrer"
      initial={{ x: -100, opacity: 0 }}
      animate={{ 
        x: 0, 
        opacity: 1,
        y: [0, -3, 0, -2, 0],
        rotate: [0, -2, 2, -1, 0],
      }}
      exit={{ x: -100, opacity: 0 }}
      transition={{ 
        x: { type: "spring", damping: 20, stiffness: 200, delay: 0.15 },
        opacity: { type: "spring", damping: 20, stiffness: 200, delay: 0.15 },
        y: { duration: 3, repeat: Infinity, repeatDelay: 4, ease: "easeInOut" },
        rotate: { duration: 3, repeat: Infinity, repeatDelay: 4, ease: "easeInOut" },
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="fixed bottom-[6.5rem] left-6 z-[90] flex items-center rounded-full bg-[#FF0000] text-white shadow-[0_4px_20px_rgba(255,0,0,0.35)] hover:shadow-[0_6px_30px_rgba(255,0,0,0.5)] transition-shadow"
      whileTap={{ scale: 0.95 }}
      data-testid="youtube-promo-badge"
    >
      <motion.div 
        className="flex items-center overflow-hidden"
        animate={{ 
          width: isHovered ? 'auto' : 44,
          paddingLeft: isHovered ? 12 : 0,
          paddingRight: isHovered ? 16 : 0,
        }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
      >
        {/* YouTube Icon Circle */}
        <div className="w-[44px] h-[44px] flex-shrink-0 flex items-center justify-center">
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-white">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
          </svg>
        </div>
        {/* Expanding Text */}
        <AnimatePresence>
          {isHovered && (
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.2 }}
              className="whitespace-nowrap font-semibold text-sm"
            >
              Subscribe to MooTube
            </motion.span>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.a>
  );
};

// Spotify / Moo Music Expandable Button Component - compact circle that expands on hover
const NewMusicButton = ({ 
  isExpanded, 
  onToggle, 
  onHangingClick, 
  onCosmixClick, 
  onMomoClick, 
  onTRKClick 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      initial={{ x: -100, opacity: 0 }}
      animate={{ 
        x: 0, 
        opacity: 1,
        y: [0, -2, 0, -3, 0],
        rotate: [0, 1, -1, 2, 0],
      }}
      exit={{ x: -100, opacity: 0 }}
      transition={{ 
        x: { type: "spring", damping: 20, stiffness: 200 },
        opacity: { type: "spring", damping: 20, stiffness: 200 },
        y: { duration: 3.5, repeat: Infinity, repeatDelay: 3.5, ease: "easeInOut", delay: 1.5 },
        rotate: { duration: 3.5, repeat: Infinity, repeatDelay: 3.5, ease: "easeInOut", delay: 1.5 },
      }}
      className="fixed bottom-6 left-6 z-[90]"
    >
      {/* Expanded Menu */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="absolute bottom-14 left-0 w-64 glass-card-dark rounded-2xl p-3 shadow-[0_10px_40px_rgba(0,0,0,0.3)]"
          >
            <div className="space-y-2">
              {/* LONDON */}
              <motion.button
                onClick={onHangingClick}
                className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-white/10 transition-colors"
                whileHover={{ x: 5 }}
              >
                <img src={LONDON_ARTWORK} alt="LONDON" className="w-10 h-10 rounded-lg object-cover" />
                <div className="text-left flex-1">
                  <p className="text-white font-semibold text-sm">LONDON</p>
                  <p className="text-white/60 text-xs">Soundmoo feat. TRK</p>
                </div>
                <span className="px-2 py-0.5 rounded-full bg-[#1D9AE8] text-white text-xs font-bold">NEW</span>
              </motion.button>
              
              {/* Cosmix */}
              <motion.button
                onClick={onCosmixClick}
                className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-white/10 transition-colors"
                whileHover={{ x: 5 }}
              >
                <img src={COSMIX_SONG_ARTWORK} alt="Accessorize" className="w-10 h-10 rounded-lg object-cover" />
                <div className="text-left flex-1">
                  <p className="text-white font-semibold text-sm">Accessorize</p>
                  <p className="text-white/60 text-xs">Cosmix Girlz</p>
                </div>
              </motion.button>
              
              {/* TRK Everywhere */}
              <motion.button
                onClick={onTRKClick}
                className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-white/10 transition-colors"
                whileHover={{ x: 5 }}
              >
                <img src={TRK_EVERYWHERE_ARTWORK} alt="Everywhere" className="w-10 h-10 rounded-lg object-cover" />
                <div className="text-left flex-1">
                  <p className="text-white font-semibold text-sm">Everywhere</p>
                  <p className="text-white/60 text-xs">The Retro Kid</p>
                </div>
                <span className="px-2 py-0.5 rounded-full bg-[#22C55E]/80 text-white text-xs font-bold">200K+</span>
              </motion.button>
              
              {/* Momo */}
              <motion.button
                onClick={onMomoClick}
                className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-white/10 transition-colors"
                whileHover={{ x: 5 }}
              >
                <img src={MOMO_ALBUM_ARTWORK} alt="Sleep & Comfort" className="w-10 h-10 rounded-lg object-cover" />
                <div className="text-left flex-1">
                  <p className="text-white font-semibold text-sm">Sleep & Comfort</p>
                  <p className="text-white/60 text-xs">Momo</p>
                </div>
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Button - compact circle, expands on hover */}
      <motion.button
        onClick={onToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="flex items-center rounded-full bg-[#1DB954] text-white shadow-[0_4px_20px_rgba(29,185,84,0.35)] hover:shadow-[0_6px_30px_rgba(29,185,84,0.5)] transition-shadow"
        whileTap={{ scale: 0.95 }}
        data-testid="new-music-button"
      >
        <motion.div 
          className="flex items-center overflow-hidden"
          animate={{ 
            width: isHovered || isExpanded ? 'auto' : 44,
            paddingLeft: isHovered || isExpanded ? 12 : 0,
            paddingRight: isHovered || isExpanded ? 16 : 0,
          }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
        >
          {/* Spotify Icon Circle */}
          <div className="w-[44px] h-[44px] flex-shrink-0 flex items-center justify-center">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
            </svg>
          </div>
          {/* Expanding Text */}
          <AnimatePresence>
            {(isHovered || isExpanded) && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
                className="whitespace-nowrap font-semibold text-sm"
              >
                New Releases
              </motion.span>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.button>
    </motion.div>
  );
};

// Expandable grass footer with logo and social links
const ExpandableGrassFooter = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      
      // Show when near bottom of page (last 30% of scroll)
      const scrollPercent = (scrollY + windowHeight) / docHeight;
      setIsVisible(scrollPercent > 0.85);
      
      // Auto-hide footer when scrolling up
      if (scrollY < lastScrollY && isExpanded) {
        setIsExpanded(false);
      }
      setLastScrollY(scrollY);
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, isExpanded]);
  
  if (!isVisible) return null;
  
  const socialLinks = [
    { 
      icon: Instagram, 
      href: 'https://instagram.com/soundmooofficial', 
      label: 'Instagram' 
    },
    { 
      icon: XIcon, 
      href: 'https://x.com/soundmoo_', 
      label: 'X' 
    },
    { 
      icon: Youtube, 
      href: 'https://www.youtube.com/@soundmoo/featured', 
      label: 'YouTube' 
    },
  ];
  
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 pointer-events-none">
      {/* Grass SVG - expandable */}
      <motion.div
        className="relative w-full"
        animate={{ height: isExpanded ? 200 : 80 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        <svg 
          viewBox="0 0 1200 150" 
          className="w-full h-full" 
          preserveAspectRatio="none"
          style={{ position: 'absolute', bottom: 0 }}
        >
          <defs>
            <linearGradient id="grassGradMain" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#4ade80" />
              <stop offset="100%" stopColor="#22c55e" />
            </linearGradient>
          </defs>
          {/* Rolling hills - solid opaque */}
          <path
            d="M0,60 Q100,30 200,45 Q350,15 500,40 Q700,10 900,35 Q1050,20 1200,40 L1200,150 L0,150 Z"
            fill="url(#grassGradMain)"
          />
          {/* Grass blades */}
          {[...Array(60)].map((_, i) => {
            const x = 8 + i * 20;
            const height = 22 + (i * 7) % 35;
            const lean = ((i * 5) % 16) - 8;
            return (
              <path
                key={i}
                d={`M ${x},150 Q ${x + lean},${150 - height * 0.6} ${x + lean * 1.3},${150 - height}`}
                stroke="#4ade80"
                strokeWidth="2"
                fill="none"
              />
            );
          })}
        </svg>
        
        {/* Expand/Collapse button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="absolute left-1/2 -translate-x-1/2 top-2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-all pointer-events-auto z-10"
          data-testid="grass-expand-button"
        >
          {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
        </button>
        
        {/* Content revealed when expanded */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-x-0 top-14 z-10 pointer-events-auto"
            >
              <div className="flex flex-col items-center gap-3 pt-2">
                {/* Social Media Buttons */}
                <div className="flex items-center gap-4">
                  {socialLinks.map((social) => (
                    <motion.a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/40 transition-all"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      aria-label={social.label}
                      data-testid={`social-${social.label.toLowerCase()}`}
                    >
                      <social.icon className="w-5 h-5" />
                    </motion.a>
                  ))}
                </div>
                
                {/* Copyright text */}
                <div className="flex flex-col md:flex-row items-center gap-1 text-xs text-white font-medium">
                  <p>© 2026 Soundmoo™. All rights reserved.</p>
                  <span className="hidden md:inline mx-2">•</span>
                  <p>All original character designs are trademarks of Soundmoo™.</p>
                </div>
                
                {/* Logo - At the very bottom, centered */}
                <div className="flex justify-center mt-2">
                  <img src={SOUNDMOO_TEXT_LOGO} alt="Soundmoo" className="h-10 md:h-12" />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

function App() {
  const [showCosmixPopup, setShowCosmixPopup] = useState(false);
  const [showMomoPopup, setShowMomoPopup] = useState(false);
  const [showTRKPopup, setShowTRKPopup] = useState(false);
  const [showHangingPopup, setShowHangingPopup] = useState(true); // New main popup
  const [showMusicMenu, setShowMusicMenu] = useState(false); // For NEW MUSIC expandable menu
  const [showConfetti, setShowConfetti] = useState(true);
  const [confettiColors, setConfettiColors] = useState(['#1D9AE8', '#9b59b6', '#FF69B4', '#ffffff', '#C0C0C0']);
  
  // Trigger confetti
  const triggerConfetti = useCallback((colors) => {
    setConfettiColors(colors);
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 4000);
  }, []);
  
  const handleCosmixMinimize = () => {
    setShowCosmixPopup(false);
  };
  
  const handleCosmixExpand = () => {
    setShowCosmixPopup(true);
    setShowMusicMenu(false);
    triggerConfetti(['#FF69B4', '#FF1493', '#FF00FF', '#00FFFF', '#00FF00', '#FFFF00']);
  };
  
  const handleMomoMinimize = () => {
    setShowMomoPopup(false);
  };
  
  const handleMomoExpand = () => {
    setShowMomoPopup(true);
    setShowMusicMenu(false);
    triggerConfetti(['#19A4FB', '#0066CC', '#00BFFF', '#87CEEB', '#4169E1', '#1E90FF']);
  };
  
  const handleTRKMinimize = () => {
    setShowTRKPopup(false);
  };
  
  const handleTRKExpand = () => {
    setShowTRKPopup(true);
    setShowMusicMenu(false);
    triggerConfetti(['#FF6B00', '#FF8C00', '#FFA500', '#22C55E', '#4ADE80', '#86EFAC']);
  };
  
  const handleHangingMinimize = () => {
    setShowHangingPopup(false);
  };
  
  const handleHangingExpand = () => {
    setShowHangingPopup(true);
    setShowMusicMenu(false);
    triggerConfetti(['#1D9AE8', '#9b59b6', '#FF69B4', '#ffffff', '#C0C0C0', '#00BFFF']);
  };
  
  const toggleMusicMenu = () => {
    setShowMusicMenu(!showMusicMenu);
  };
  
  // Check if any popup is open
  const anyPopupOpen = showHangingPopup || showCosmixPopup || showMomoPopup || showTRKPopup;
  
  return (
    <div className="App min-h-screen relative" data-testid="app-container">
      {/* Confetti Effect */}
      <AnimatePresence>
        {showConfetti && <Confetti colors={confettiColors} />}
      </AnimatePresence>
      
      {/* LONDON Release Popup - Main popup on load */}
      <AnimatePresence>
        {showHangingPopup && <LondonPopupAd onMinimize={handleHangingMinimize} />}
      </AnimatePresence>
      
      {/* Cosmix Popup Advertisement */}
      <AnimatePresence>
        {showCosmixPopup && <CosmixPopupAd onMinimize={handleCosmixMinimize} />}
      </AnimatePresence>
      
      {/* Momo Popup Advertisement */}
      <AnimatePresence>
        {showMomoPopup && <MomoPopupAd onMinimize={handleMomoMinimize} />}
      </AnimatePresence>
      
      {/* TRK Popup Advertisement */}
      <AnimatePresence>
        {showTRKPopup && <TRKPopupAd onMinimize={handleTRKMinimize} />}
      </AnimatePresence>
      
      {/* NEW MUSIC Expandable Button - Only show when no popup is open */}
      <AnimatePresence>
        {!anyPopupOpen && (
          <>
            <YouTubePromoBadge />
            <NewMusicButton
              isExpanded={showMusicMenu}
              onToggle={toggleMusicMenu}
              onHangingClick={handleHangingExpand}
              onCosmixClick={handleCosmixExpand}
              onMomoClick={handleMomoExpand}
              onTRKClick={handleTRKExpand}
            />
          </>
        )}
      </AnimatePresence>
      
      {/* Dream → Morning Background Layer (behind everything) */}
      <DreamBackground />
      
      {/* Sun Layer (behind content but above background) */}
      <SkyForeground />
      
      {/* Content Layer */}
      <div className="relative z-10">
        <Navbar />
        <main>
          <HeroSection />
          <WhySoundmoo />
          <CharacterShowcase />
          <SoundIdentity />
          <Partnerships />
          <ContactSection />
        </main>
        {/* Extra padding at bottom for grass */}
        <div className="h-32" />
      </div>
      
      {/* Expandable Grass Footer with Logo & Social Links */}
      <ExpandableGrassFooter />
      
      {/* Go-karts — absolute at page bottom, z-55 IN FRONT of grass z-50, scrolls with page */}
      <div className="absolute bottom-0 left-0 right-0 z-[55] pointer-events-none" style={{ height: 180 }} data-testid="bottom-gokart-characters">
        <BottomGoKarts />
      </div>
      
      {/* Cookie Consent */}
      <CookieConsent />
    </div>
  );
}

export default App;
